import { Container } from '@playcanvas/pcui';
import { Vec3 } from 'playcanvas';

import { Events } from '../events';
import { Scene } from '../scene';
import { CoordWorldMarker, formatCoordDmsLines } from './coord-marker-entity';
import { pointerPositionInElement } from './pointer-coordinates';
import {
    openCoordEntryHoverCard,
    cancelActiveCoordEntryHoverIfAny,
    isCoordEntryHoverCardOpen
} from '../ui/coord-entry-card';
import { RfiRecord } from '../ui/rfi-panel';

const MAX_COORD_MARKERS = 32;

function dmsToDecimalCoordTool(deg: string, min: string, sec: string, ref: string): number | null {
    const d = String(deg ?? '').trim();
    const m = String(min ?? '').trim();
    const s = String(sec ?? '').trim();
    if (d === '' && m === '' && s === '') return null;
    const dv = d === '' ? 0 : Number(d);
    const mv = m === '' ? 0 : Number(m);
    const sv = s === '' ? 0 : Number(s);
    if (!Number.isFinite(dv) || !Number.isFinite(mv) || !Number.isFinite(sv)) return null;
    let dec = Math.abs(dv) + mv / 60 + sv / 3600;
    const r = String(ref || '').toUpperCase();
    if (r === 'S' || r === 'W') dec = -dec;
    return dec;
}

function decimalToDmsPartsCoordTool(deg: number, kind: 'lat' | 'lng'): { d: number; m: number; s: number; ref: string } {
    const abs = Math.abs(deg);
    const d = Math.floor(abs);
    const mFloat = (abs - d) * 60;
    const m = Math.floor(mFloat);
    const sec = (mFloat - m) * 60;
    const ref = kind === 'lat' ? (deg >= 0 ? 'N' : 'S') : deg >= 0 ? 'E' : 'W';
    return { d, m, s: sec, ref };
}

function clampLatLngCoordTool(lat: number, lng: number): { lat: number; lng: number } | null {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return { lat, lng };
}

function cameraViewFromScene(scene: Scene): { position: number[]; target: number[] } | null {
    const pos = scene.camera.mainCamera?.getPosition?.();
    const fp = scene.camera.focalPointTween?.value;
    if (!pos || !fp) return null;
    return {
        position: [pos.x, pos.y, pos.z],
        target: [fp.x, fp.y, fp.z]
    };
}

function genId(): string {
    return `coord_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Ground-control coordinate markers — separate from RFI pins; same Firestore `annotations` with `kind: 'coord'`.
 */
class CoordTool {
    activate: () => void;
    deactivate: () => void;

    constructor(events: Events, scene: Scene, canvasContainer: Container) {
        const markersHost = document.createElement('div');
        markersHost.className = 'coord-markers-host';
        markersHost.style.cssText =
            'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none;z-index:36;';
        canvasContainer.dom.appendChild(markersHost);

        const addGcpHint = document.createElement('div');
        addGcpHint.className = 'coord-add-gcp-hint';
        addGcpHint.hidden = true;
        addGcpHint.setAttribute('aria-hidden', 'true');
        addGcpHint.textContent = '+ ADD GCP';
        markersHost.appendChild(addGcpHint);

        let addGcpHintMove: ((ev: PointerEvent) => void) | null = null;
        let addGcpKeydown: ((ev: KeyboardEvent) => void) | null = null;

        function positionAddGcpHint(clientX: number, clientY: number) {
            const hostRect = markersHost.getBoundingClientRect();
            if (hostRect.width === 0 || hostRect.height === 0) return;
            const left = clientX - hostRect.left + 14;
            const top = clientY - hostRect.top + 18;
            addGcpHint.style.left = `${left}px`;
            addGcpHint.style.top = `${top}px`;
        }

        function teardownAddGcpHintUi() {
            canvasContainer.dom.style.removeProperty('cursor');
            addGcpHint.hidden = true;
            addGcpHint.setAttribute('aria-hidden', 'true');
            if (addGcpHintMove) {
                canvasContainer.dom.removeEventListener('pointermove', addGcpHintMove);
                addGcpHintMove = null;
            }
            if (addGcpKeydown) {
                window.removeEventListener('keydown', addGcpKeydown, true);
                addGcpKeydown = null;
            }
        }

        events.on('volugraph.mapPlot.gcpAddArmed', (on: unknown) => {
            teardownAddGcpHintUi();
            if (on !== true) return;
            canvasContainer.dom.style.cursor = 'crosshair';
            addGcpHint.hidden = false;
            addGcpHint.removeAttribute('aria-hidden');
            const move = (ev: PointerEvent) => {
                positionAddGcpHint(ev.clientX, ev.clientY);
            };
            addGcpHintMove = move;
            canvasContainer.dom.addEventListener('pointermove', move);
            const kd = (ev: KeyboardEvent) => {
                if (ev.key === 'Escape') {
                    ev.preventDefault();
                    try {
                        events.invoke('volugraph.mapPlot.setGcpAddArmed', false);
                    } catch {
                        /* ignore */
                    }
                }
            };
            addGcpKeydown = kd;
            window.addEventListener('keydown', kd, true);
        });

        const records: RfiRecord[] = [];
        const markers = new Map<string, CoordWorldMarker>();

        let active = false;
        let clickGate = false;
        let suppressCanvasDeselectClick = false;
        let isEditMode = false;
        let selectedId: string | null = null;

        let hoverCardEl: HTMLDivElement | null = null;
        let hoverCardRecId: string | null = null;
        /** Serialize GCP hover draft sessions so cancel/replace does not clear map-plot pending too early. */
        let gcpHoverDraftGeneration = 0;

        /** Map-plot GCP step: splat picked, waiting for map pill confirm or typed card. */
        let splitPillPending: {
            draft: CoordWorldMarker;
            worldVec: Vec3;
            gcpN: number;
            gen: number;
        } | null = null;

        function clearSplitPillDraft(): void {
            if (!splitPillPending) return;
            try {
                splitPillPending.draft.destroy();
            } catch {
                /* ignore */
            }
            splitPillPending = null;
        }

        events.removeFunction('coord.export');
        events.function('coord.export', () => records.map(r => ({ ...r })));

        events.removeFunction('coord.entryMode');
        events.function('coord.entryMode', () => 'modal');

        events.removeFunction('coord.extraFieldsForNewMarker');
        events.function('coord.extraFieldsForNewMarker', () => ({}));

        try {
            events.removeFunction('volugraph.mapPlot.coordCardOpen');
        } catch {
            /* ignore */
        }
        events.function('volugraph.mapPlot.coordCardOpen', () => isCoordEntryHoverCardOpen());

        /** Host stays non-capturing; individual markers receive clicks only in editor mode. */
        function syncMarkerInteractivity() {
            markersHost.style.pointerEvents = 'none';
            const pe = isEditMode ? 'auto' : 'none';
            markers.forEach((m) => {
                m.hitEl.style.pointerEvents = pe;
                if (m.labelEl.classList.contains('coord-marker-label--interactive')) {
                    m.labelEl.style.pointerEvents = pe;
                    if (isEditMode) {
                        m.labelEl.setAttribute('tabindex', '0');
                    } else {
                        m.labelEl.removeAttribute('tabindex');
                    }
                }
            });
            if (!isEditMode && selectedId) {
                selectedId = null;
                hideHoverCard();
                syncMarkerLabelSelection();
            }
        }

        function positionHoverCard(anchorEl: HTMLElement) {
            if (!hoverCardEl) return;
            const hostRect = markersHost.getBoundingClientRect();
            if (hostRect.width === 0 || hostRect.height === 0) return;
            const circleRect = anchorEl.getBoundingClientRect();
            const gap = 8;
            const left = circleRect.right - hostRect.left + gap;
            const top = circleRect.top + circleRect.height / 2 - hostRect.top;
            hoverCardEl.style.left = `${left}px`;
            hoverCardEl.style.top = `${top}px`;
        }

        function hideHoverCard() {
            if (hoverCardEl) {
                hoverCardEl.remove();
                hoverCardEl = null;
                hoverCardRecId = null;
            }
        }

        function updateLayouts() {
            markers.forEach(m => m.updateScreenLayout());
            if (hoverCardEl && hoverCardRecId) {
                const m = markers.get(hoverCardRecId);
                if (m && !m.hitEl.classList.contains('coord-marker-hidden')) {
                    positionHoverCard(m.hitEl);
                } else {
                    hideHoverCard();
                }
            }
        }

        function flyTo(id: string) {
            const rec = records.find(x => x.id === id);
            if (!rec) return;
            const worldPos = new Vec3(rec.position[0], rec.position[1], rec.position[2]);
            scene.camera.setFocalPoint(worldPos);
            scene.forceRender = true;
        }

        function wireHit(recId: string, hitEl: HTMLButtonElement, labelEl: HTMLDivElement) {
            const arm = (el: HTMLElement) => {
                el.addEventListener('pointerdown', e => e.stopPropagation());
                el.addEventListener('pointerup', e => e.stopPropagation());
                el.addEventListener('click', (e) => {
                    e.stopPropagation();
                    if (!isEditMode) return;
                    setSelected(selectedId === recId ? null : recId);
                });
                el.addEventListener('dblclick', (e) => {
                    e.stopPropagation();
                    flyTo(recId);
                });
            };
            arm(hitEl);
            if (labelEl.classList.contains('coord-marker-label--interactive')) {
                arm(labelEl);
                labelEl.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        (labelEl as HTMLElement).click();
                    }
                });
            }
        }

        function syncMarkerLabelSelection(): void {
            markers.forEach((m, id) => {
                if (m.labelEl.classList.contains('coord-marker-label--interactive')) {
                    m.labelEl.classList.toggle('coord-marker-label--selected', id === selectedId);
                }
            });
        }

        function setSelected(id: string | null) {
            selectedId = id;
            hideHoverCard();
            if (id) {
                const m = markers.get(id);
                if (m) showHoverCard(id, m.hitEl);
            }
            syncMarkerLabelSelection();
        }

        function showHoverCard(recId: string, anchorEl: HTMLElement) {
            if (hoverCardEl && hoverCardRecId === recId) return;
            hideHoverCard();
            const rec = records.find(x => x.id === recId);
            if (!rec || rec.kind !== 'coord') return;

            hoverCardRecId = recId;
            const card = document.createElement('div');
            card.className = isEditMode ?
                'coord-hover-card coord-hover-card--edit annotation-hover-card' :
                'coord-hover-card annotation-hover-card annotation-hover-card--view';

            if (!isEditMode) {
                const label = document.createElement('div');
                label.className = 'coord-hover-readonly';
                label.style.fontFamily = 'ui-monospace,Consolas,monospace';
                label.style.whiteSpace = 'pre';
                if (typeof rec.lat === 'number' && typeof rec.lng === 'number') {
                    label.textContent = formatCoordDmsLines(rec.lat, rec.lng);
                } else {
                    label.textContent = '—';
                }
                card.appendChild(label);
                if (typeof rec.ele === 'number' && Number.isFinite(rec.ele)) {
                    const el = document.createElement('div');
                    el.className = 'coord-hover-ele';
                    el.textContent = `Elev: ${rec.ele} m`;
                    card.appendChild(el);
                }
            } else {
                const stack = document.createElement('div');
                stack.className = 'coord-hover-dms-stack';

                const makeDmsAxis = (axisLabel: string, refId: 'lat' | 'lng', initLat: number, initLng: number) => {
                    const field = document.createElement('div');
                    field.className = 'coord-hover-dms-field';
                    const lab = document.createElement('span');
                    lab.className = 'coord-hover-dms-label';
                    lab.textContent = axisLabel;
                    const row = document.createElement('div');
                    row.className = 'coord-entry-dms-row';
                    const parts =
                        refId === 'lat'
                            ? decimalToDmsPartsCoordTool(initLat, 'lat')
                            : decimalToDmsPartsCoordTool(initLng, 'lng');
                    const deg = document.createElement('input');
                    deg.type = 'number';
                    deg.className = 'coord-entry-input-compact';
                    deg.placeholder = '°';
                    deg.value = String(parts.d);
                    const minIn = document.createElement('input');
                    minIn.type = 'number';
                    minIn.className = 'coord-entry-input-compact';
                    minIn.placeholder = "'";
                    minIn.value = String(parts.m);
                    const sec = document.createElement('input');
                    sec.type = 'number';
                    sec.step = 'any';
                    sec.className = 'coord-entry-input-compact';
                    sec.placeholder = '"';
                    sec.value = String(parts.s);
                    const ref = document.createElement('select');
                    ref.className = 'coord-hover-ref-select';
                    ref.setAttribute('aria-label', refId === 'lat' ? 'North or South' : 'East or West');
                    ref.innerHTML =
                        refId === 'lat' ? '<option value="N">N</option><option value="S">S</option>' : '<option value="E">E</option><option value="W">W</option>';
                    ref.value = parts.ref;
                    const unitDeg = document.createElement('span');
                    unitDeg.className = 'coord-hover-dms-unit';
                    unitDeg.textContent = '°';
                    const unitMin = document.createElement('span');
                    unitMin.className = 'coord-hover-dms-unit';
                    unitMin.textContent = "'";
                    const unitSec = document.createElement('span');
                    unitSec.className = 'coord-hover-dms-unit';
                    unitSec.textContent = '"';
                    row.appendChild(deg);
                    row.appendChild(unitDeg);
                    row.appendChild(minIn);
                    row.appendChild(unitMin);
                    row.appendChild(sec);
                    row.appendChild(unitSec);
                    row.appendChild(ref);
                    field.appendChild(lab);
                    field.appendChild(row);
                    return { field, deg, min: minIn, sec, ref };
                };

                const latDms = makeDmsAxis(
                    'Latitude',
                    'lat',
                    typeof rec.lat === 'number' && Number.isFinite(rec.lat) ? rec.lat : 0,
                    0
                );
                const lngDms = makeDmsAxis(
                    'Longitude',
                    'lng',
                    0,
                    typeof rec.lng === 'number' && Number.isFinite(rec.lng) ? rec.lng : 0
                );
                stack.appendChild(latDms.field);
                stack.appendChild(lngDms.field);

                const eleField = document.createElement('div');
                eleField.className = 'coord-hover-dms-field coord-hover-ele-field';
                const eleLab = document.createElement('span');
                eleLab.className = 'coord-hover-dms-label';
                eleLab.textContent = 'Elevation';
                const eleInputRow = document.createElement('div');
                eleInputRow.className = 'coord-hover-ele-input-row';
                const eleIn = document.createElement('input');
                eleIn.type = 'number';
                eleIn.step = 'any';
                eleIn.className = 'coord-hover-input coord-hover-ele-input';
                eleIn.placeholder = 'Optional';
                eleIn.setAttribute('aria-label', 'Elevation, optional');
                eleIn.value =
                    typeof rec.ele === 'number' && Number.isFinite(rec.ele) ? String(rec.ele) : '';
                const eleUnit = document.createElement('select');
                eleUnit.className = 'coord-hover-ref-select coord-hover-ele-unit-select';
                eleUnit.setAttribute('aria-label', 'Elevation unit');
                eleUnit.innerHTML = '<option value="m">m</option><option value="ft">ft</option>';
                let elePrevUnit: 'm' | 'ft' = 'm';
                eleUnit.addEventListener('change', () => {
                    const v = Number(eleIn.value);
                    const next = eleUnit.value === 'ft' ? 'ft' : 'm';
                    if (Number.isFinite(v) && elePrevUnit !== next) {
                        if (elePrevUnit === 'm' && next === 'ft') {
                            eleIn.value = String(Math.round((v / 0.3048) * 100000) / 100000);
                        } else if (elePrevUnit === 'ft' && next === 'm') {
                            eleIn.value = String(Math.round(v * 0.3048 * 100000) / 100000);
                        }
                    }
                    elePrevUnit = next;
                });

                eleInputRow.appendChild(eleIn);
                eleInputRow.appendChild(eleUnit);
                eleField.appendChild(eleLab);
                eleField.appendChild(eleInputRow);
                stack.appendChild(eleField);

                const row = document.createElement('div');
                row.className = 'coord-hover-actions';
                const saveBtn = document.createElement('button');
                saveBtn.type = 'button';
                saveBtn.textContent = 'SAVE';
                saveBtn.className = 'coord-hover-save';
                saveBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const la = dmsToDecimalCoordTool(latDms.deg.value, latDms.min.value, latDms.sec.value, latDms.ref.value);
                    const lo = dmsToDecimalCoordTool(lngDms.deg.value, lngDms.min.value, lngDms.sec.value, lngDms.ref.value);
                    if (la === null || lo === null) return;
                    const cl = clampLatLngCoordTool(la, lo);
                    if (!cl) return;
                    rec.lat = cl.lat;
                    rec.lng = cl.lng;
                    const es = eleIn.value.trim();
                    if (es === '') {
                        delete rec.ele;
                    } else {
                        const en = Number(es);
                        if (Number.isFinite(en)) {
                            rec.ele = eleUnit.value === 'ft' ? en * 0.3048 : en;
                        }
                    }
                    markers.get(recId)?.setLatLng(rec.lat, rec.lng);
                    upsertRecord(rec, false);
                    // Defer teardown so removing the card mid–event queue does not drop the
                    // click onto the canvas. Skip if the user already selected another marker.
                    suppressCanvasDeselectClick = true;
                    const closedForId = recId;
                    requestAnimationFrame(() => {
                        if (selectedId !== closedForId && hoverCardRecId !== closedForId) {
                            return;
                        }
                        hideHoverCard();
                        if (selectedId === closedForId) {
                            setSelected(null);
                        } else {
                            syncMarkerLabelSelection();
                        }
                    });
                });

                const delBtn = document.createElement('button');
                delBtn.type = 'button';
                delBtn.className = 'coord-hover-delete annotation-hover-delete';
                delBtn.title = 'Delete coordinate marker';
                delBtn.setAttribute('aria-label', 'Delete coordinate marker');
                delBtn.innerHTML =
                    '<svg class="annotation-hover-delete-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"/></svg>';
                delBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    removeRecord(recId, false);
                });

                row.appendChild(saveBtn);
                row.appendChild(delBtn);
                card.appendChild(stack);
                card.appendChild(row);
            }

            ['pointerdown', 'pointerup', 'pointermove', 'click', 'dblclick', 'wheel'].forEach((name) => {
                card.addEventListener(name, e => e.stopPropagation());
            });

            markersHost.appendChild(card);
            hoverCardEl = card;
            positionHoverCard(anchorEl);
            requestAnimationFrame(() => positionHoverCard(anchorEl));
        }

        function removeRecord(id: string, fromRemote: boolean) {
            const idx = records.findIndex(x => x.id === id);
            if (idx >= 0) records.splice(idx, 1);
            markers.get(id)?.destroy();
            markers.delete(id);
            if (selectedId === id) selectedId = null;
            hideHoverCard();
            syncMarkerLabelSelection();
            scene.forceRender = true;
            updateLayouts();
            if (!fromRemote) {
                events.fire('annotation.requestBumpDirty');
                events.fire('volugraph.rfi', { action: 'delete', id });
            }
        }

        try {
            events.removeFunction('coord.clearAlignmentGcpMarkers');
        } catch {
            /* ignore */
        }
        events.function('coord.clearAlignmentGcpMarkers', () => {
            const ids = records.filter(r => r.kind === 'coord' && r.usedAsGcp === true).map(r => r.id);
            for (const id of ids) {
                removeRecord(id, false);
            }
            clearSplitPillDraft();
            cancelActiveCoordEntryHoverIfAny();
            hideHoverCard();
            return ids.length;
        });

        function upsertRecord(rec: RfiRecord, fromRemote: boolean, newMarkerGcpIndex?: number) {
            if (rec.kind !== 'coord') return;
            if (typeof rec.lat !== 'number' || typeof rec.lng !== 'number') return;

            const idx = records.findIndex(x => x.id === rec.id);
            if (idx >= 0) {
                records[idx] = rec;
            } else {
                records.push(rec);
            }

            let m = markers.get(rec.id);
            if (!m) {
                const markerOpts =
                    typeof newMarkerGcpIndex === 'number' && Number.isFinite(newMarkerGcpIndex) && newMarkerGcpIndex > 0 ?
                        { labelIndex: Math.floor(newMarkerGcpIndex) } :
                        undefined;
                m = new CoordWorldMarker(
                    scene,
                    markersHost,
                    rec.id,
                    new Vec3(rec.position[0], rec.position[1], rec.position[2]),
                    rec.lat,
                    rec.lng,
                    markerOpts
                );
                wireHit(rec.id, m.hitEl, m.labelEl);
                markers.set(rec.id, m);
                syncMarkerInteractivity();
            } else {
                m.setWorldPosition(rec.position[0], rec.position[1], rec.position[2]);
                m.setLatLng(rec.lat, rec.lng);
            }

            if (!fromRemote) {
                events.fire('annotation.requestBumpDirty');
                events.fire('volugraph.rfi', {
                    action: 'upsert',
                    record: { ...rec }
                });
            }
            scene.forceRender = true;
            updateLayouts();
            applyMarkersGlobalVisibility();
        }

        const positionTupleEqual = (
            a: [number, number, number],
            b: [number, number, number]
        ) => a[0] === b[0] && a[1] === b[1] && a[2] === b[2];

        const parseCoordImportRecord = (raw: unknown): RfiRecord | null => {
            if (!raw || typeof raw !== 'object') return null;
            const o = raw as Record<string, unknown>;
            if (o.kind !== 'coord') return null;

            const id = typeof o.id === 'string' ? o.id : genId();
            let px = 0;
            let py = 0;
            let pz = 0;
            if (Array.isArray(o.position) && o.position.length === 3) {
                px = Number(o.position[0]) || 0;
                py = Number(o.position[1]) || 0;
                pz = Number(o.position[2]) || 0;
            } else if (o.position && typeof o.position === 'object') {
                const p = o.position as Record<string, number>;
                px = Number(p.x) || 0;
                py = Number(p.y) || 0;
                pz = Number(p.z) || 0;
            }
            const latRaw = o.lat;
            const lngRaw = o.lng;
            if (typeof latRaw !== 'number' || !Number.isFinite(latRaw)) return null;
            if (typeof lngRaw !== 'number' || !Number.isFinite(lngRaw)) return null;

            const cv = o.cameraView as RfiRecord['cameraView'];
            const ord = o.orderIndex;
            const eleRaw = o.ele;
            const usedAsGcp = o.usedAsGcp === true ? true : undefined;

            const rec: RfiRecord = {
                id,
                kind: 'coord',
                position: [px, py, pz],
                title: '',
                text: '',
                imageUrl: '',
                status: 'neutral',
                tags: [],
                cameraView:
                    cv && Array.isArray(cv.position) && Array.isArray(cv.target) ? cv : null,
                orderIndex: typeof ord === 'number' && Number.isFinite(ord) ? ord : undefined,
                lat: latRaw,
                lng: lngRaw,
                ...(usedAsGcp ? { usedAsGcp: true } : {})
            };
            if (typeof eleRaw === 'number' && Number.isFinite(eleRaw)) rec.ele = eleRaw;
            return rec;
        };

        const applyIncomingCoordFields = (target: RfiRecord, incoming: RfiRecord) => {
            target.position = [...incoming.position];
            target.lat = incoming.lat;
            target.lng = incoming.lng;
            target.cameraView = incoming.cameraView;
            target.orderIndex = incoming.orderIndex;
            if (incoming.ele !== undefined) target.ele = incoming.ele;
            else delete target.ele;
            if (incoming.usedAsGcp) target.usedAsGcp = true;
            else delete target.usedAsGcp;
        };

        let markersGloballyVisible = true;
        const applyMarkersGlobalVisibility = () => {
            markersHost.classList.toggle('coord-markers-host--hidden', !markersGloballyVisible);
            if (!markersGloballyVisible) {
                hideHoverCard();
            } else {
                updateLayouts();
            }
        };
        events.on('annotation.setMarkersVisible', (visible: boolean) => {
            markersGloballyVisible = visible !== false;
            applyMarkersGlobalVisibility();
        });

        function importAnnotations(list: unknown) {
            if (!Array.isArray(list)) return;

            const incoming: RfiRecord[] = [];
            for (const raw of list) {
                const rec = parseCoordImportRecord(raw);
                if (rec) incoming.push(rec);
            }

            const prevSel = selectedId;
            const currentIds = new Set(records.map(r => r.id));
            const incomingIds = new Set(incoming.map(r => r.id));
            const sameIdSet =
                currentIds.size === incomingIds.size
                && [...incomingIds].every(id => currentIds.has(id));

            let positionsStable = sameIdSet;
            if (positionsStable) {
                for (const inc of incoming) {
                    const cur = records.find(r => r.id === inc.id);
                    if (!cur || !positionTupleEqual(cur.position, inc.position)) {
                        positionsStable = false;
                        break;
                    }
                }
            }

            if (sameIdSet && positionsStable && records.length > 0) {
                const byId = new Map(records.map(r => [r.id, r]));
                for (const inc of incoming) {
                    const cur = byId.get(inc.id);
                    if (!cur) continue;
                    applyIncomingCoordFields(cur, inc);
                    const m = markers.get(inc.id);
                    if (m) {
                        m.setWorldPosition(inc.position[0], inc.position[1], inc.position[2]);
                        if (typeof inc.lat === 'number' && typeof inc.lng === 'number') {
                            m.setLatLng(inc.lat, inc.lng);
                        }
                    }
                }
                scene.forceRender = true;
                updateLayouts();
                syncMarkerLabelSelection();
                applyMarkersGlobalVisibility();
                return;
            }

            records.length = 0;
            markers.forEach(m => m.destroy());
            markers.clear();
            selectedId = null;
            hideHoverCard();
            clearSplitPillDraft();

            for (const rec of incoming) {
                upsertRecord(rec, true);
            }

            scene.forceRender = true;
            updateLayouts();
            syncMarkerInteractivity();
            if (prevSel && isEditMode && records.some(r => r.id === prevSel)) {
                setSelected(prevSel);
            } else {
                syncMarkerLabelSelection();
            }
            applyMarkersGlobalVisibility();
        }

        events.on('volugraph.importAnnotations', importAnnotations);
        applyMarkersGlobalVisibility();

        events.on('postrender', updateLayouts);

        events.on('volugraph.mapPlot.pairConfirm', (p: Record<string, unknown>) => {
            if (!splitPillPending) return;
            const nPayload = typeof p?.n === 'number' && Number.isFinite(p.n) ? p.n : null;
            if (nPayload === null || nPayload !== splitPillPending.gcpN) return;
            const lat = typeof p?.lat === 'number' ? p.lat : NaN;
            const lng = typeof p?.lng === 'number' ? p.lng : NaN;
            if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                return;
            }
            const sp = p?.splat;
            if (Array.isArray(sp) && sp.length === 3 && sp.every(x => typeof x === 'number' && Number.isFinite(x))) {
                const w = splitPillPending.worldVec;
                const eps = 1e-4;
                if (
                    Math.abs(sp[0] - w.x) > eps ||
                    Math.abs(sp[1] - w.y) > eps ||
                    Math.abs(sp[2] - w.z) > eps
                ) {
                    return;
                }
            }
            const { draft, worldVec, gcpN, gen } = splitPillPending;
            if (gen !== gcpHoverDraftGeneration) return;
            splitPillPending = null;
            try {
                draft.destroy();
            } catch {
                /* ignore */
            }
            const entryEle = typeof p?.ele === 'number' && Number.isFinite(p.ele) ? p.ele : undefined;
            const entry: { lat: number; lng: number; ele?: number } = {
                lat,
                lng,
                ...(entryEle !== undefined ? { ele: entryEle } : {})
            };

            const cam = cameraViewFromScene(scene);
            const id = genId();
            let maxOrder = 0;
            for (const r of records) {
                if (typeof r.orderIndex === 'number' && r.orderIndex > maxOrder) maxOrder = r.orderIndex;
            }
            const extras = events.invoke('coord.extraFieldsForNewMarker') as Record<string, unknown> | undefined;
            const rec: RfiRecord = {
                id,
                kind: 'coord',
                position: [worldVec.x, worldVec.y, worldVec.z],
                title: '',
                text: '',
                imageUrl: '',
                status: 'neutral',
                tags: [],
                cameraView: cam,
                orderIndex: maxOrder + 1,
                lat: entry.lat,
                lng: entry.lng,
                ...(entry.ele !== undefined ? { ele: entry.ele } : {}),
                ...(extras && typeof extras === 'object' ? (extras as Partial<RfiRecord>) : {})
            };

            suppressCanvasDeselectClick = true;
            try {
                events.invoke('volugraph.mapPlot.notifyGcpDraftEnd');
            } catch {
                /* ignore */
            }
            upsertRecord(rec, false, gcpN);
            scene.forceRender = true;
            updateLayouts();
        });

        events.on('volugraph.mapPlot.pendingGcp', (detail: unknown) => {
            if (detail !== null && detail !== undefined) return;
            clearSplitPillDraft();
        });

        events.on('volugraph.mapPlot.requestTypedGcpEntry', () => {
            void (async () => {
                if (!splitPillPending) {
                    events.fire('volugraph.mapPlot.toast', { message: 'Pick a point on the 3D model first.' });
                    return;
                }
                const snap = splitPillPending;
                cancelActiveCoordEntryHoverIfAny();
                const hit = snap.draft.hitEl;
                const br = hit.getBoundingClientRect();
                const clientX = br.left + br.width / 2;
                const clientY = br.top + br.height / 2;
                const entry = await openCoordEntryHoverCard({
                    host: markersHost,
                    clientX,
                    clientY,
                    gcpIndex: snap.gcpN,
                    awaitingMapHint: true,
                    onArmMapPick(setLatLng) {
                        events.invoke('volugraph.mapPlot.registerCoordCardSetter', setLatLng);
                        events.invoke('volugraph.mapPlot.setGcpMapPickArmed', true);
                    }
                });
                if (!entry) {
                    scene.forceRender = true;
                    updateLayouts();
                    return;
                }
                if (!splitPillPending || splitPillPending.gen !== snap.gen) {
                    return;
                }
                try {
                    splitPillPending.draft.destroy();
                } catch {
                    /* ignore */
                }
                splitPillPending = null;
                const cam = cameraViewFromScene(scene);
                const id = genId();
                let maxOrder = 0;
                for (const r of records) {
                    if (typeof r.orderIndex === 'number' && r.orderIndex > maxOrder) maxOrder = r.orderIndex;
                }
                const extras = events.invoke('coord.extraFieldsForNewMarker') as Record<string, unknown> | undefined;
                const newRec: RfiRecord = {
                    id,
                    kind: 'coord',
                    position: [snap.worldVec.x, snap.worldVec.y, snap.worldVec.z],
                    title: '',
                    text: '',
                    imageUrl: '',
                    status: 'neutral',
                    tags: [],
                    cameraView: cam,
                    orderIndex: maxOrder + 1,
                    lat: entry.lat,
                    lng: entry.lng,
                    ...(entry.ele !== undefined ? { ele: entry.ele } : {}),
                    ...(extras && typeof extras === 'object' ? (extras as Partial<RfiRecord>) : {})
                };
                suppressCanvasDeselectClick = true;
                try {
                    events.invoke('volugraph.mapPlot.notifyGcpDraftEnd');
                } catch {
                    /* ignore */
                }
                upsertRecord(newRec, false, snap.gcpN);
                scene.forceRender = true;
                updateLayouts();
            })();
        });

        const volugraphRoot =
            canvasContainer.dom.closest?.('[data-volugraph-supersplat]')
            ?? canvasContainer.dom.closest?.('[data-volugraph-embed]')
            ?? document.body;
        const syncEditModeFromAttr = () => {
            isEditMode = volugraphRoot.getAttribute?.('data-edit-mode') === 'true';
        };
        syncEditModeFromAttr();

        events.on('volugraph.editModeChange', (editMode: boolean) => {
            isEditMode = editMode;
            syncMarkerInteractivity();
            if (isEditMode && selectedId) {
                const m = markers.get(selectedId);
                if (m) showHoverCard(selectedId, m.hitEl);
            }
            syncMarkerLabelSelection();
        });

        syncMarkerInteractivity();

        const isPrimary = (e: PointerEvent) => (e.pointerType === 'mouse' ? e.button === 0 : e.isPrimary);

        const pointerdown = (e: PointerEvent) => {
            if (isPrimary(e)) clickGate = true;
        };
        const pointermove = () => {
            clickGate = false;
        };

        const pointerup = async (e: PointerEvent) => {
            if (!active || !clickGate || !isPrimary(e)) return;
            clickGate = false;
            const t = e.target as HTMLElement;
            if (t.closest?.('.rfi-panel')) return;
            if (t.closest?.('.annotation-numbered-marker')) return;
            if (t.closest?.('.coord-marker-hit')) return;
            if (t.closest?.('.coord-marker-label--interactive')) return;
            if (t.closest?.('.STARLING-MapPlotChrome')) return;
            if (t.closest?.('.coord-hover-card')) return;
            if (t.closest?.('.annotation-hover-card')) return;
            if (t.closest?.('.annotation-modal-overlay')) return;
            if (t.closest?.('.coord-entry-modal-overlay')) return;
            if (t.closest?.('.coord-entry-hover-card')) return;
            if (t.closest?.('.annotation-calibration-modal')) return;
            if (t.closest?.('#left-annotation-toolbar')) return;

            if (records.length >= MAX_COORD_MARKERS) {
                return;
            }

            const modeEarly = events.invoke('coord.entryMode') as string | undefined;
            if (modeEarly === 'hover' && !splitPillPending) {
                const canStart = events.invoke('volugraph.mapPlot.isGcpAddArmed') as boolean | undefined;
                if (!canStart) {
                    return;
                }
            }

            const ptr = pointerPositionInElement(e, scene.canvas);
            const hit = await scene.camera.intersect(ptr.nx, ptr.ny);
            if (!hit) return;

            const mode = events.invoke('coord.entryMode') as string | undefined;
            const world = hit.position;
            let entry: { lat: number; lng: number; ele?: number } | null = null;
            let hoverGcpN: number | undefined;

            if (mode !== 'hover') {
                const aligned = events.invoke('volugraph.coord.hasGoodGisAlignment') === true;
                if (!aligned) return;
            }

            if (mode === 'hover') {
                if (splitPillPending) {
                    splitPillPending.draft.setWorldPosition(world.x, world.y, world.z);
                    splitPillPending.worldVec = new Vec3(world.x, world.y, world.z);
                    scene.forceRender = true;
                    updateLayouts();
                    events.fire('volugraph.mapPlot.gcpDraftStarted', {
                        n: splitPillPending.gcpN,
                        splat: [world.x, world.y, world.z]
                    });
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }
                const myGen = ++gcpHoverDraftGeneration;
                cancelActiveCoordEntryHoverIfAny();
                const gcpN = events.invoke('mapPlot.allocGcpNumber') as number | null;
                hoverGcpN = typeof gcpN === 'number' && Number.isFinite(gcpN) ? gcpN : undefined;
                if (hoverGcpN === undefined) {
                    return;
                }
                const draft = new CoordWorldMarker(
                    scene,
                    markersHost,
                    `draft_${genId()}`,
                    new Vec3(world.x, world.y, world.z),
                    0,
                    0,
                    {
                        draft: true,
                        labelIndex: hoverGcpN
                    }
                );
                try {
                    scene.forceRender = true;
                    updateLayouts();
                    events.fire('volugraph.mapPlot.gcpDraftStarted', {
                        n: hoverGcpN,
                        splat: [world.x, world.y, world.z]
                    });
                    splitPillPending = {
                        draft,
                        worldVec: new Vec3(world.x, world.y, world.z),
                        gcpN: hoverGcpN,
                        gen: myGen
                    };
                } catch {
                    draft.destroy();
                    splitPillPending = null;
                    scene.forceRender = true;
                    updateLayouts();
                    return;
                }
                scene.forceRender = true;
                updateLayouts();
                e.preventDefault();
                e.stopPropagation();
                return;
            }

            const projected = events.invoke(
                'volugraph.coord.projectScenePointToLatLng',
                world.x,
                world.y,
                world.z
            ) as { lat: number; lng: number } | null | undefined;
            if (
                !projected
                || typeof projected.lat !== 'number'
                || typeof projected.lng !== 'number'
                || !Number.isFinite(projected.lat)
                || !Number.isFinite(projected.lng)
            ) {
                return;
            }
            entry = { lat: projected.lat, lng: projected.lng };

            const cam = cameraViewFromScene(scene);
            const id = genId();
            let maxOrder = 0;
            for (const r of records) {
                if (typeof r.orderIndex === 'number' && r.orderIndex > maxOrder) maxOrder = r.orderIndex;
            }
            const extras = events.invoke('coord.extraFieldsForNewMarker') as Record<string, unknown> | undefined;
            const rec: RfiRecord = {
                id,
                kind: 'coord',
                position: [world.x, world.y, world.z],
                title: '',
                text: '',
                imageUrl: '',
                status: 'neutral',
                tags: [],
                cameraView: cam,
                orderIndex: maxOrder + 1,
                lat: entry.lat,
                lng: entry.lng,
                ...(entry.ele !== undefined ? { ele: entry.ele } : {}),
                ...(extras && typeof extras === 'object' ? (extras as Partial<RfiRecord>) : {})
            };

            upsertRecord(rec, false, hoverGcpN);
            suppressCanvasDeselectClick = true;
            requestAnimationFrame(() => {
                setSelected(id);
            });
            e.preventDefault();
            e.stopPropagation();
            if (typeof hoverGcpN === 'number' && hoverGcpN < 3) {
                events.fire('tool.coord');
            } else {
                events.fire('tool.deactivate');
            }
        };

        const onCanvasClickDeselect = (e: MouseEvent) => {
            if (suppressCanvasDeselectClick) {
                suppressCanvasDeselectClick = false;
                return;
            }
            if (!selectedId) return;
            const t = e.target as HTMLElement;
            if (t.closest?.('.coord-marker-hit')) return;
            if (t.closest?.('.coord-marker-label--interactive')) return;
            if (t.closest?.('.coord-hover-card')) return;
            if (t.closest?.('.annotation-numbered-marker')) return;
            if (t.closest?.('.annotation-hover-card')) return;
            if (t.closest?.('.rfi-panel')) return;
            if (t.closest?.('#left-annotation-toolbar')) return;
            if (t.closest?.('.annotation-modal-overlay')) return;
            if (t.closest?.('.coord-entry-modal-overlay')) return;
            if (t.closest?.('.coord-entry-hover-card')) return;
            if (t.closest?.('.annotation-calibration-modal')) return;
            if (t.closest?.('#bottom-toolbar')) return;
            if (t.closest?.('.select-toolbar')) return;
            setSelected(null);
        };
        canvasContainer.dom.addEventListener('click', onCanvasClickDeselect);

        this.activate = () => {
            active = true;
            canvasContainer.dom.classList.add('volugraph-coord-tool-active');
            canvasContainer.dom.style.cursor = 'crosshair';
            syncMarkerInteractivity();
            canvasContainer.dom.addEventListener('pointerdown', pointerdown);
            canvasContainer.dom.addEventListener('pointermove', pointermove);
            canvasContainer.dom.addEventListener('pointerup', pointerup, true);
        };

        this.deactivate = () => {
            active = false;
            canvasContainer.dom.classList.remove('volugraph-coord-tool-active');
            syncMarkerInteractivity();
            teardownAddGcpHintUi();
            canvasContainer.dom.removeEventListener('pointerdown', pointerdown);
            canvasContainer.dom.removeEventListener('pointermove', pointermove);
            canvasContainer.dom.removeEventListener('pointerup', pointerup, true);
        };
    }
}

export { CoordTool };
